/**
 * Comprehensive cookie utility functions
 *
 * @example
 * ```typescript
 * import { cookie } from '@packages/ui-components';
 *
 * // Basic cookie operations
 * cookie.setCookie('user-preference', 'dark-mode', 7); // expires in 7 days
 * const preference = cookie.getCookie('user-preference');
 * const hasPreference = cookie.hasCookie('user-preference');
 * cookie.removeCookie('user-preference');
 *
 * // Feature-specific operations
 * const shouldDisable = cookie.shouldDisableInteractiveFeatures();
 * ```
 */

/**
 * Checks if a cookie with the given name exists
 * @param name - The name of the cookie to check
 * @returns boolean - True if the cookie exists, false otherwise
 */
const hasCookie = (name: string): boolean => {
  if (typeof document === 'undefined') return false;

  const cookies = document.cookie.split(';');
  for (let i = 0; i < cookies.length; i++) {
    const cookie = cookies[i].trim();
    if (cookie.startsWith(name + '=')) {
      return true;
    }
  }
  return false;
};

/**
 * Gets the value of a cookie with the given name
 * @param name - The name of the cookie to get
 * @returns string | null - The value of the cookie, or null if it doesn't exist
 */
const getCookie = (name: string): string | null => {
  if (typeof document === 'undefined') return null;

  const cookies = document.cookie.split(';');
  for (let i = 0; i < cookies.length; i++) {
    const cookie = cookies[i].trim();
    if (cookie.startsWith(name + '=')) {
      return cookie.substring(name.length + 1);
    }
  }
  return null;
};

/**
 * Sets a cookie with the given name and value
 * @param name - The name of the cookie to set
 * @param value - The value to set
 * @param days - Optional number of days until the cookie expires
 */
const setCookie = (name: string, value: string, days?: number): void => {
  if (typeof document === 'undefined') return;

  let expires = '';
  if (days) {
    const date = new Date();
    date.setTime(date.getTime() + days * 24 * 60 * 60 * 1000);
    expires = `; expires=${date.toUTCString()}`;
  }
  document.cookie = `${name}=${value}${expires}; path=/`;
};

/**
 * Removes a cookie with the given name
 * @param name - The name of the cookie to remove
 */
const removeCookie = (name: string): void => {
  if (typeof document === 'undefined') return;

  document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`;
};

/**
 * Checks if the grade cookie indicates that interactive features should be disabled
 * @returns boolean - True if interactive features should be disabled
 */
const shouldDisableInteractiveFeatures = (yzerId: number | undefined | null): boolean => {
  if(!yzerId) return false;
  if(yzerId !== 11867 && yzerId !== 16218 && yzerId !== 15863) return false;
  const grade = getCookie('y2018-2-cohort');
  return grade !== null && parseInt(grade) > 50;
};

/**
 * Cookie utility object with comprehensive cookie management functions
 */
export const cookie = {
  /**
   * Checks if a cookie with the given name exists
   */
  hasCookie,

  /**
   * Gets the value of a cookie with the given name
   */
  getCookie,

  /**
   * Sets a cookie with the given name and value
   */
  setCookie,

  /**
   * Removes a cookie with the given name
   */
  removeCookie,

  /**
   * Checks if the grade cookie indicates that interactive features should be disabled
   */
  shouldDisableInteractiveFeatures,
} as const;

export default cookie;
